// Pages/LeadReport.tsx  — with dynamic filter support
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import UpdateLeadModal from "../Components/EditLeadModalDemo";
import ExportLeads, { LeadReportFilter } from "../Components/ExportLeads";
import { GetLeadReportLog } from "../Services/APIService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import {
    fetchLeadDataById,
    fetchLocationData,
} from "../Services/APIService";
import OverlayLoader from "../Components/OverlayLoader";

// ─── Interfaces ────────────────────────────────────────────────────────────────

interface LeadLogRow {
    slNo: number;
    leadId: number;
    fullName: string;
    currentAssignedUserName: string;
    sourceName: string;
    subSourceName: string;
    currentStatusName: string;
    createdOn: string;
    reconnectedOn: string;
    lastFollowupDate: string;
}

interface Lead {
    leadId: number;
    firstName: string;
    lastName: string;
    gender: string;
    dob: string;
    dom: string;
    mobile: string;
    email: string;
    address: string;
    stateId: number;
    districtId: number;
    cityId: number;
    zipCode: number | string;
    sourceId: number;
    subSourceId: number;
    currentStatus: number;
    incomeFrom: number | string;
    incomeTo: number | string;
    initialAssignedUser: number;
    currentAssignedUser: number;
    budgetMin?: number;
    budgetMax?: number;
    tenantId: number;
    modifiedBy?: number;
    assignType: string;
    initialAssignedUserName: string;
    currentAssignedUserName: string;
    sourceName: string;
    subSourceName: string;
    currentStatusName: string;
    createdOn: string;

    countryCode: string;
    alternateMobile: string;
    alternateMobileCountryCode: string;
}

interface Location {
    locationID: number;
    locationName: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDatetime = (date: Date | string): string => {
    const dateObj = typeof date === "string"
        ? new Date(String(date).endsWith("Z") ? date : String(date).replace(" ", "T"))
        : date;
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return "Invalid Date";
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    let hours = dateObj.getHours();
    const mins = String(dateObj.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
};

// ─── Unwrap API response → plain array ────────────────────────────────────────

const unwrapLogs = (raw: any): any[] => {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.Logs)) return raw.Logs;
    if (Array.isArray(raw?.logs)) return raw.logs;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.result)) return raw.result;
    return [];
};

// ─── Normalize a log entry so field names are consistent ──────────────────────

const normalizeLog = (log: any): any => ({
    ...log,
    sourceId: log.incomingSource ?? log.lead?.sourcE_ID ?? log.lead?.sourceId ?? null,
    subSourceId: log.incomingSubsourceId ?? log.lead?.suB_SOURCE_ID ?? log.lead?.subSourceId ?? null,
    projectId: log.interestedProject ?? log.project?.projectId ?? null,
    followupTypeId: log.followupConducted ?? log.followupType?.id ?? null,
});

// ─── Transform raw log array → one row per lead ───────────────────────────────

const transformLogsToLeads = (logs: any[]): LeadLogRow[] => {
    const grouped: Record<number, any[]> = {};
    logs.forEach(log => {
        if (!grouped[log.leadId]) grouped[log.leadId] = [];
        grouped[log.leadId].push(log);
    });

    return Object.values(grouped).map((leadLogs, index) => {
        const sorted = leadLogs.sort(
            (a, b) => new Date(a.createdOn ?? 0).getTime() - new Date(b.createdOn ?? 0).getTime()
        );
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const reconnected = sorted.find(l => l.eventTypeName === "Reconnected");

        const L = first.lead ?? last.lead ?? {};

        return {
            slNo: index + 1,
            leadId: first.leadId ?? 0,
            fullName: L.fullName
                ?? `${L.firsT_NAME ?? L.firstName ?? ""} ${L.lasT_NAME ?? L.lastName ?? ""}`.trim()
                ?? "-",
            currentAssignedUserName: last.assignedUserName ?? "-",
            sourceName: first.incomingSourceName ?? L.sourcE_NAME ?? L.SOURCE_NAME ?? "-",
            subSourceName: last.incomingSubsourceName ?? last.incomingSourceName ?? "-",
            currentStatusName: L.currenT_STATUS_NAME ?? L.CURRENT_STATUS_NAME ?? last.statusName ?? "-",
            createdOn: first.createdOn ?? "",
            reconnectedOn: reconnected?.createdOn ?? "-",
            lastFollowupDate: last.createdOn ?? "",
        };
    });
};

// ─── Client-side filter ───────────────────────────────────────────────────────

const applyFilter = (logs: any[], f: LeadReportFilter): any[] => {
    return logs.filter(log => {
        if (f.dateFrom || f.dateTo) {
            const ts = new Date(log.createdOn ?? 0).getTime();
            if (f.dateFrom && ts < new Date(f.dateFrom).getTime()) return false;
            if (f.dateTo && ts > new Date(f.dateTo).getTime()) return false;
        }
        if (f.followupTypeIds.length && !f.followupTypeIds.includes(log.followupTypeId)) return false;
        if (f.projectIds.length && !f.projectIds.includes(log.projectId)) return false;
        if (f.sourceIds.length && !f.sourceIds.includes(log.sourceId)) return false;
        if (f.subSourceIds.length && !f.subSourceIds.includes(log.subSourceId)) return false;
        if (f.teamIds.length && !f.teamIds.includes(log.assignedUserGroupId)) return false;
        return true;
    });
};

// ─── Component ────────────────────────────────────────────────────────────────

const LeadReport: React.FC = () => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [leads, setLeads] = useState<LeadLogRow[]>([]);
    const [rawLogs, setRawLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchText, setSearchText] = useState("");
    const [locations, setLocations] = useState<Location[]>([]);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [editLeadData, setEditLeadData] = useState<Lead | null>(null);
    const [isFilterOpen, setFilterOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState<LeadReportFilter | null>(null);

    const navigate = useNavigate();
    const location = useLocation();

    // ── Active-filter count ───────────────────────────────────────────────────
    const filterCount = activeFilters
        ? (activeFilters.dateFrom ? 1 : 0)
        + activeFilters.followupTypeIds.length
        + activeFilters.projectIds.length
        + activeFilters.sourceIds.length
        + activeFilters.subSourceIds.length
        + activeFilters.teamIds.length
        : 0;

    // ── Fetch logs ────────────────────────────────────────────────────────────
    const fetchLeads = async (filters?: LeadReportFilter): Promise<void> => {
        try {
            setLoading(true);

            const params: Record<string, any> = {};
            if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
            if (filters?.dateTo) params.dateTo = filters.dateTo;
            if (filters?.followupTypeIds?.length) params.followupTypeIds = filters.followupTypeIds.join(",");
            if (filters?.projectIds?.length) params.projectIds = filters.projectIds.join(",");
            if (filters?.sourceIds?.length) params.sourceIds = filters.sourceIds.join(",");
            if (filters?.subSourceIds?.length) params.subSourceIds = filters.subSourceIds.join(",");
            if (filters?.teamIds?.length) params.teamIds = filters.teamIds.join(",");
            if (filters?.userIds?.length) params.userIds = filters.userIds.join(",");
            if (filters?.campaignIds?.length) params.campaignIds = filters.campaignIds.join(",");

            const raw: any = await GetLeadReportLog(params);

            const logs = unwrapLogs(raw).map(normalizeLog);

            setRawLogs(logs);
            setLeads(
                transformLogsToLeads(logs)
                    .sort((a, b) => b.leadId - a.leadId)
                    .map((row, i) => ({ ...row, slNo: i + 1 }))
            );
        } catch (err) {
            console.error("Fetch error:", err);
            setError(err instanceof Error ? err.message : "An unknown error occurred");
        } finally {
            setLoading(false);
        }
    };

    // ── Filter handlers ───────────────────────────────────────────────────────
    const handleFilterSubmit = (filters: LeadReportFilter) => {
        setActiveFilters(filters);
        fetchLeads(filters);
    };

    const handleClearFilters = () => {
        setActiveFilters(null);
        fetchLeads();
    };

    // ── Export Excel ──────────────────────────────────────────────────────────
    const handleExportExcel = async () => {
        try {
            if (filteredLeads.length === 0) { toast.error("No data to export"); return; }

            const logsByLead: Record<number, any[]> = {};
            rawLogs.forEach((log: any) => {
                if (!logsByLead[log.leadId]) logsByLead[log.leadId] = [];
                logsByLead[log.leadId].push(log);
            });
            Object.values(logsByLead).forEach((arr: any[]) =>
                arr.sort((a, b) => new Date(a.createdOn ?? 0).getTime() - new Date(b.createdOn ?? 0).getTime())
            );

            const sortedLogs = filteredLeads.map(tableRow => {
                const all = logsByLead[tableRow.leadId] ?? [];
                const first = all[0] ?? {};
                const last = all[all.length - 1] ?? {};
                const L = first.lead ?? last.lead ?? {};
                const firstFollowup = all.find((l: any) => l.followupConducted !== 0) ?? null;
                const allFollowups = all.filter((l: any) => l.followupConducted !== 0);
                const lastFollowup = allFollowups[allFollowups.length - 1] ?? null;
                return { first, last, firstFollowup, lastFollowup, L, all };
            });

            if (sortedLogs.length === 0) { toast.error("No data to export"); return; }

            const fmtDate = (v: any): string => {
                if (!v) return "";
                try {
                    const s = String(v);
                    const d = new Date(s.endsWith("Z") ? s : s + "Z");
                    return isNaN(d.getTime()) ? s : formatDatetime(d);
                } catch (_e) { return String(v); }
            };

            const na = (v: any): string =>
                (v === null || v === undefined || v === "") ? "N/A" : String(v);

            const naDate = (v: any): string => {
                if (!v) return "N/A";
                try {
                    const s = String(v);
                    const d = new Date(s.endsWith("Z") ? s : s.replace(" ", "T"));
                    return isNaN(d.getTime()) ? "N/A" : formatDatetime(d);
                } catch (_e) { return "N/A"; }
            };

            const dataRows = sortedLogs.map(({ first, last, firstFollowup, lastFollowup, L }: any) => ({
                "Lead's Id": first.leadId ?? "N/A",
                "First Name": na(L.firsT_NAME ?? L.firstName ?? L.FIRST_NAME ?? first.firstName),
                "Last Name": na(L.lasT_NAME ?? L.lastName ?? L.LAST_NAME ?? first.lastName),
                "Lead Status": na(L.currenT_STATUS_NAME ?? L.CURRENT_STATUS_NAME ?? last.statusName),
                "Country Code": (L.countrY_CODE ?? L.COUNTRY_CODE),
                "Phone": na(L.mobile ?? L.MOBILE),
                "Alternate Country Code": na(L.alternatE_COUNTRY_CODE ?? L.ALTERNATE_COUNTRY_CODE),
                "Alternate Phone": na(L.alternatE_MOBILE_NO ?? L.ALTERNATE_MOBILE_NO),
                "Email": na(L.email ?? L.EMAIL),
                "Address": na(L.address ?? L.ADDRESS),
                "City": na(L.citY_NAME ?? L.CITY_NAME),
                "Region": na(L.statE_NAME ?? L.STATE_NAME),
                "Zip Code": na(L.ziP_CODE ?? L.ZIP_CODE),
                "Min Budget": na(L.budgeT_MIN ?? L.BUDGET_MIN),
                "Max Budget": na(L.budgeT_MAX ?? L.BUDGET_MAX),
                "Date Of Birth": naDate(L.dob ?? L.DOB),
                "Gender": na(L.gender ?? L.GENDER),
                "Created At(System Date)": naDate(L.createD_ON ?? L.CREATED_ON),
                "Lead Modified On": naDate(L.modifieD_ON ?? L.MODIFIED_ON),
                "Attended By": na(first.assignedUserName),
                "First-Source Of Enquiry": na(first.incomingSourceName ?? L.sourcE_NAME ?? L.SOURCE_NAME),
                "First-Sub Source": na(first.incomingSubsourceName ?? L.suB_SOURCE_NAME ?? L.SUB_SOURCE_NAME),
                "First Campaign Project": na(first.interestedProjectName),
                "First-Campaign": na(first.campaignName),
                "First-Medium": na(first.mediaTypeName),
                "First Log Notes": na(first.logNotes),
                "First Task Note": na(first.taskNote),
                "Followup Status": first.isNextActionRequired === true ? "Conducted" : first.isNextActionRequired === false ? "Scheduled" : "N/A",
                "Received On": naDate(first.createdOn),
                "Attended By Sales Id": na(firstFollowup?.assignedUserName),
                "First contacted Attempted on": na(firstFollowup?.eventTypeName),
                "First contacted on": naDate(firstFollowup?.createdOn),
                "First Followup Source": na(firstFollowup?.incomingSourceName),
                "First Followup Project": na(firstFollowup?.interestedProjectName),
                "First Followup Notes": na(firstFollowup?.logNotes),
                "First Followup Task Note": na(firstFollowup?.taskNote),
                "First Followup Medium": na(firstFollowup?.mediaTypeName),
                "First Followup Next Action": firstFollowup?.isNextActionRequired === true ? "Conducted" : firstFollowup?.isNextActionRequired === false ? "Scheduled" : "N/A",
                "Last Followup Type": na(lastFollowup?.followupTypeName),
                "Last Followup Status": na(lastFollowup?.statusName),
                "Last-Source Of Enquiry": na(lastFollowup?.incomingSourceName),
                "Last-Sub Source": na(lastFollowup?.incomingSubsourceName),
                "Last Campaign Project": na(lastFollowup?.interestedProjectName),
                "Last-Campaign": na(lastFollowup?.campaignName),
                "Last-Medium": na(lastFollowup?.mediaTypeName),
                "Last Followup Task Note": na(lastFollowup?.taskNote),
                "Last Followup Next Action": lastFollowup?.isNextActionRequired === true ? "Conducted" : lastFollowup?.isNextActionRequired === false ? "Scheduled" : "N/A",
                "Last Followup Date": naDate(lastFollowup?.createdOn),
                "Attended By Team": na(last.assignedUserName),
                "Deal stage": na(last.statusName),
                "Last Contacted On": naDate(last.createdOn),
                "Last-Source Of Enquiry (Current)": na(last.incomingSourceName),
                "Last-Sub Source (Current)": na(last.incomingSubsourceName),
                "Interested in": na(last.interestedProjectName),
                "Channel Partner": na(last.campaignName),
                "Current Log Notes": na(last.logNotes),
                "Current Task Note": na(last.taskNote),
                "Last Log Created On": naDate(last.createdOn),
            }));

            if (dataRows.length === 0) { toast.error("No data to export"); return; }

            const headers = Object.keys(dataRows[0]);
            const numCols = headers.length;
            const DATA_ROW = 10;

            const applyStyle = (ws: any, addr: string, s: any) => {
                if (!ws[addr]) ws[addr] = { t: "s", v: "" };
                ws[addr].s = { ...(ws[addr].s ?? {}), ...s };
            };
            const DARK = "1F3864"; const MID = "2E75B6";
            const LBLUE = "D6E4F0"; const LGRAY = "F2F2F2";
            const bdr = {
                top: { style: "thin", color: { rgb: "BFBFBF" } },
                bottom: { style: "thin", color: { rgb: "BFBFBF" } },
                left: { style: "thin", color: { rgb: "BFBFBF" } },
                right: { style: "thin", color: { rgb: "BFBFBF" } },
            };
            const sTitleBanner = { font: { name: "Arial", sz: 16, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: DARK } }, alignment: { horizontal: "center", vertical: "center" } };
            const sCritLabel = { font: { name: "Arial", sz: 10, bold: true, color: { rgb: MID } }, alignment: { horizontal: "left", vertical: "center" } };
            const sCritValue = { font: { name: "Arial", sz: 10, color: { rgb: "1F1F1F" } }, fill: { patternType: "solid", fgColor: { rgb: LBLUE } }, alignment: { horizontal: "left", vertical: "center" }, border: bdr };
            const sSectionBar = { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: MID } }, alignment: { horizontal: "left", vertical: "center" } };
            const sColHdr = { font: { name: "Arial", sz: 10, bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: DARK } }, alignment: { horizontal: "center", vertical: "center", wrapText: true }, border: { top: { style: "medium", color: { rgb: MID } }, bottom: { style: "medium", color: { rgb: MID } }, left: { style: "thin", color: { rgb: "BFBFBF" } }, right: { style: "thin", color: { rgb: "BFBFBF" } } } };
            const sBody = (alt: boolean, ctr = false) => ({ font: { name: "Arial", sz: 10, color: { rgb: "1F1F1F" } }, fill: { patternType: "solid", fgColor: { rgb: alt ? LGRAY : "FFFFFF" } }, alignment: { horizontal: ctr ? "center" : "left", vertical: "center" }, border: bdr });

            const ws: any = {};

            const now = new Date();
            const pad = (n: number) => String(n).padStart(2, "0");
            const h12 = now.getHours() % 12 || 12;
            const ap = now.getHours() >= 12 ? "PM" : "AM";
            const nowStr = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(h12)}:${pad(now.getMinutes())} ${ap}`;

            ws[XLSX.utils.encode_cell({ r: 0, c: 0 })] = { t: "s", v: "LEAD REPORT" };
            applyStyle(ws, XLSX.utils.encode_cell({ r: 0, c: 0 }), sTitleBanner);

            const criteria: [string, string | number][] = [
                ["Export Of", "Lead Report"],
                ["List", "All Leads"],
                ["Export Date", nowStr],
                ["Duration", activeFilters?.dateFrom ? `${activeFilters.dateFrom} — ${activeFilters.dateTo ?? "now"}` : "All Time"],
                ["Number of Records", filteredLeads.length],
            ];
            criteria.forEach(([lbl, val], i) => {
                const aA = XLSX.utils.encode_cell({ r: i + 1, c: 0 });
                const aB = XLSX.utils.encode_cell({ r: i + 1, c: 1 });
                ws[aA] = { t: "s", v: lbl };
                applyStyle(ws, aA, sCritLabel);
                ws[aB] = { t: typeof val === "number" ? "n" : "s", v: val };
                applyStyle(ws, aB, sCritValue);
            });

            ws[XLSX.utils.encode_cell({ r: 8, c: 0 })] = { t: "s", v: "  LEAD LOG DATA  (sorted by Lead ID — Descending)" };
            applyStyle(ws, XLSX.utils.encode_cell({ r: 8, c: 0 }), sSectionBar);

            headers.forEach((hdr, c) => {
                const addr = XLSX.utils.encode_cell({ r: 9, c });
                ws[addr] = { t: "s", v: hdr };
                applyStyle(ws, addr, sColHdr);
            });

            dataRows.forEach((row: any, ri: number) => {
                const alt = ri % 2 === 1;
                headers.forEach((hdr, c) => {
                    const addr = XLSX.utils.encode_cell({ r: DATA_ROW + ri, c });
                    const val = row[hdr];
                    const t = typeof val === "number" ? "n" : "s";
                    ws[addr] = { t, v: val ?? "" };
                    applyStyle(ws, addr, sBody(alt, c === 0));
                });
            });

            ws["!merges"] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
                { s: { r: 8, c: 0 }, e: { r: 8, c: numCols - 1 } },
                ...criteria.map((_, i) => ({ s: { r: i + 1, c: 1 }, e: { r: i + 1, c: 4 } })),
            ];

            ws["!cols"] = headers.map((h, i) => {
                if (i === 0) return { wch: 12 };
                if (i === 2 || i === 3) return { wch: 16 };
                if (h === "Email") return { wch: 28 };
                if (h === "Address") return { wch: 30 };
                if (h === "Created At(System Date)" || h.includes("Date") || h.includes("On")) return { wch: 22 };
                if (h.startsWith("First-") || h.startsWith("Last-") || h.startsWith("First ") || h.startsWith("Last ")) return { wch: 22 };
                if (i >= 95) return { wch: 20 };
                return { wch: 18 };
            });

            ws["!rows"] = [
                { hpt: 36 }, { hpt: 18 }, { hpt: 18 }, { hpt: 18 }, { hpt: 18 }, { hpt: 18 },
                { hpt: 6 }, { hpt: 6 }, { hpt: 20 }, { hpt: 28 },
            ];

            ws["!freeze"] = { xSplit: 0, ySplit: DATA_ROW };
            ws["!autofilter"] = { ref: `A10:${XLSX.utils.encode_col(numCols - 1)}10` };
            ws["!ref"] = `A1:${XLSX.utils.encode_col(numCols - 1)}${DATA_ROW + dataRows.length}`;

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, ws, "Leads Report");
            const buf = XLSX.write(workbook, { bookType: "xlsx", type: "array", cellStyles: true });
            saveAs(
                new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
                `Lead_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
            );

        } catch (err) {
            console.error("Export error:", err);
            toast.error("Failed to export Excel");
        }
    };

    // ── Effects ───────────────────────────────────────────────────────────────
    useEffect(() => { fetchLeads(); }, []);

    useEffect(() => {
        const getMenus = async () => {
            setIsLoading(true);
            try {
                const menuID = location.state?.menuID;
                const previousMenuID = localStorage.getItem("activeMenuID");
                if (menuID !== previousMenuID || menuID === null)
                    localStorage.setItem("activeMenuID", menuID);
                else
                    localStorage.removeItem("activeMenuID");
            } finally {
                setIsLoading(false);
            }
        };
        getMenus();
    }, [location]);

    useEffect(() => {
        fetchLocationData().then(setLocations).catch(console.error);
    }, []);

    useEffect(() => {
        const editLeadId = location.state?.editLeadId;
        if (editLeadId) {
            handleEdit(editLeadId);
            navigate(location.pathname, { replace: true });
        }
    }, [location.state]);

    useEffect(() => {
        if (location.state?.refresh) {
            fetchLeads(activeFilters ?? undefined);
            navigate(location.pathname, { replace: true });
        }
        if (location.state?.message) {
            toast.success(location.state.message);
            navigate(location.pathname, { replace: true });
        }
    }, [location.state]);

    // ── Edit lead ─────────────────────────────────────────────────────────────
    const handleEdit = async (leadId: number) => {
        try {
            const response = await fetchLeadDataById(leadId);
            const userId = parseInt(localStorage.getItem("UserId") ?? "0");
            const tenantId = parseInt(localStorage.getItem("tenantId") ?? "0");
            if (!tenantId) throw new Error("Tenant ID is missing");
            if (response) {
                setEditLeadData({
                    ...response,
                    tenantId,
                    modifiedBy: userId,
                    assignType: response.initialAssignedUser === userId ? "self" : "assign",
                    countryCode: response.countryCode || "+91",
                    alternateMobile: response.alternateMobile || "",
                    alternateMobileCountryCode: response.alternateMobileCountryCode || "+91",
                });
                setEditModalOpen(true);
            }
        } catch {
            toast.error("Failed to fetch lead data for editing");
        }
    };

    const leadDashbordRedirection = (leadId: number) =>
        navigate("/LeadDashboard", { state: { leadId } });

    // ── Search ────────────────────────────────────────────────────────────────
    const filteredLeads = leads.filter(lead => {
        const text = searchText.toLowerCase();
        return (
            lead.fullName?.toLowerCase().includes(text) ||
            lead.currentAssignedUserName?.toLowerCase().includes(text) ||
            String(lead.leadId).includes(text)
        );
    });

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns = [
        { name: "Sl No", selector: (row: any) => row.slNo, width: "80px" },
        { name: "Lead ID", selector: (row: any) => row.leadId, sortable: true },
        {
            name: "Lead Name",
            cell: (row: any) => {
                const initial = row.fullName?.charAt(0)?.toUpperCase() || "-";
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "rgb(167,133,144)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 10, fontWeight: "bold" }}>
                            {initial}
                        </div>
                        <a
                            href="#"
                            onClick={e => { e.preventDefault(); e.stopPropagation(); leadDashbordRedirection(row.leadId); }}
                            style={{ textDecoration: "none", fontWeight: 500, color: "#33475b" }}
                        >
                            {row.fullName}
                        </a>
                    </div>
                );
            },
            width: "250px",
        },
        { name: "Assign User", selector: (row: any) => row.currentAssignedUserName, sortable: true },
        { name: "First Source", selector: (row: any) => row.sourceName },
        { name: "Last Source", selector: (row: any) => row.subSourceName },
        { name: "Current Status", selector: (row: any) => row.currentStatusName },
        { name: "First Created On", cell: (row: any) => formatDatetime(row.createdOn) },
        { name: "Reconnected On", cell: (row: any) => row.reconnectedOn !== "-" ? formatDatetime(row.reconnectedOn) : "-" },
        { name: "Last Followup", cell: (row: any) => formatDatetime(row.lastFollowupDate) },
    ];

    if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

    return (
        <div className="wrapper">
            <div>
                <Toaster position="top-center" reverseOrder={false} containerStyle={{ zIndex: 10000 }} />
            </div>
            {!isSidebarCollapsed && <CollapsibleSidebar />}
            <div className="main">
                <Navbar onToggleSidebar={() => setIsSidebarCollapsed(v => !v)} />
                <div className="main-content-div">
                    <main className="content mainContent">
                        <div className="container-fluid p-0">
                            <div className="row">
                                <div className="col-12 col-lg-12">
                                    <div className="card">
                                        <div className="card-header carHeaderWithBtn">
                                            <div className="card-title mb-0">Leads Report</div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <input
                                                    type="text"
                                                    placeholder="Search by Lead Name or ID"
                                                    className="p-2 w-full datatableSearchInput"
                                                    value={searchText}
                                                    onChange={e => setSearchText(e.target.value)}
                                                />

                                                {/* Active filter chips */}
                                                {filterCount > 0 && (
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                        <span style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, whiteSpace: "nowrap" }}>
                                                            {filterCount} filter{filterCount !== 1 ? "s" : ""} active
                                                        </span>
                                                        <button
                                                            onClick={handleClearFilters}
                                                            style={{ background: "#ede4f7", color: "#7c3aed", border: "none", borderRadius: 4, padding: "3px 8px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}
                                                        >
                                                            ✕ Clear
                                                        </button>
                                                    </div>
                                                )}

                                                <button type="button" className="btn btn-success btn-sm" onClick={handleExportExcel}>
                                                    <i className="bi bi-file-earmark-excel"></i> Export
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm"
                                                    onClick={() => setFilterOpen(true)}
                                                    style={{
                                                        background: filterCount > 0 ? "#7c3aed" : undefined,
                                                        color: filterCount > 0 ? "#fff" : undefined,
                                                        border: filterCount > 0 ? "none" : undefined,
                                                        position: "relative",
                                                    }}
                                                >
                                                    <i className="bi bi-funnel"></i> Filter
                                                    {filterCount > 0 && (
                                                        <span style={{ position: "absolute", top: -6, right: -6, background: "#ef4444", color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            {filterCount}
                                                        </span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="card-body p-0">
                                            <DataTable
                                                columns={columns}
                                                data={filteredLeads}
                                                pagination
                                                paginationPerPage={10}
                                                highlightOnHover
                                                striped
                                                responsive
                                                noDataComponent="No Leads found"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Edit Lead Modal */}
                        {isEditModalOpen && editLeadData && (
                            <UpdateLeadModal
                                isOpen={isEditModalOpen}
                                onClose={() => { setEditModalOpen(false); setEditLeadData(null); }}
                                leadsData={editLeadData}
                                onSubmit={async () => {
                                    setEditModalOpen(false);
                                    setEditLeadData(null);
                                    toast.success("Lead updated successfully");
                                    await fetchLeads(activeFilters ?? undefined);
                                }}
                            />
                        )}

                        {/*
                          ── KEY FIX ──────────────────────────────────────────
                          BEFORE: {isFilterOpen && <ExportLeads ... />}
                            → conditional render unmounts the component every
                              time the drawer closes, wiping all filter state.

                          AFTER: always rendered, isOpen prop controls visibility.
                            → component stays mounted, state is never lost.
                              Only "Clear all filters" button inside the drawer
                              resets the selections.
                          ─────────────────────────────────────────────────────
                        */}
                        <ExportLeads
                            isOpen={isFilterOpen}
                            onClose={() => setFilterOpen(false)}
                            onSubmit={handleFilterSubmit}
                        />

                    </main>
                </div>
                <Footer />
            </div>
            <OverlayLoader loading={loading} message="Loading Lead Data" submessage="This might take a moment..." />
        </div>
    );
};

export default LeadReport;